import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Text,
    Checkbox,
    Input,
    Button,
    Stack,
    Flex,
    HStack,
    IconButton,
    VStack
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, CopyIcon, WarningIcon } from '@chakra-ui/icons';

const DAYS_OF_WEEK = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
];

const DEFAULT_SCHEDULE = {
    0: { blocks: [] }, // Sunday
    1: { blocks: [{ start: '09:00', end: '17:00' }] }, // Monday
    2: { blocks: [{ start: '09:00', end: '17:00' }] }, // Tuesday
    3: { blocks: [{ start: '09:00', end: '17:00' }] }, // Wednesday
    4: { blocks: [{ start: '09:00', end: '17:00' }] }, // Thursday
    5: { blocks: [{ start: '09:00', end: '17:00' }] }, // Friday
    6: { blocks: [] } // Saturday
};

const parseTime = (time12) => {
    const match = time12.match(/(\d{2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return time12;

    let [, hours, minutes, ampm] = match;
    hours = parseInt(hours);

    if (ampm.toUpperCase() === 'PM' && hours !== 12) {
        hours += 12;
    } else if (ampm.toUpperCase() === 'AM' && hours === 12) {
        hours = 0;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes}`;
};

const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
};

const hasTimeConflict = (blocks) => {
    if (blocks.length <= 1) return [];

    const conflicts = [];
    const sortedBlocks = blocks.map((block, index) => ({ ...block, originalIndex: index }))
        .sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));

    for (let i = 0; i < sortedBlocks.length; i++) {
        const current = sortedBlocks[i];
        const currentStart = timeToMinutes(current.start);
        const currentEnd = timeToMinutes(current.end);

        if (currentStart >= currentEnd) {
            conflicts.push(current.originalIndex);
            continue;
        }

        for (let j = i + 1; j < sortedBlocks.length; j++) {
            const next = sortedBlocks[j];
            const nextStart = timeToMinutes(next.start);
            const nextEnd = timeToMinutes(next.end);

            if (nextStart >= nextEnd) {
                conflicts.push(next.originalIndex);
                continue;
            }

            if (currentEnd > nextStart) {
                conflicts.push(current.originalIndex, next.originalIndex);
            }
        }
    }

    return [...new Set(conflicts)];
};

const AvailabilitySchedule = ({ schedule, onScheduleChange }) => {
    const [availabilitySchedule, setAvailabilitySchedule] = useState(DEFAULT_SCHEDULE);
    const [conflictingBlocks, setConflictingBlocks] = useState({});

    const isAvailable = (daySchedule) => {
        if (daySchedule && daySchedule.blocks && daySchedule.blocks.length > 0) {
            return true;
        }
        return false;
    };

    const updateConflicts = useCallback((scheduleToCheck) => {
        const newConflicts = {};
        let hasAnyConflict = false;

        for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
            const daySchedule = scheduleToCheck[dayIndex];
            if (isAvailable(daySchedule)) {
                const conflicts = hasTimeConflict(daySchedule.blocks);
                if (conflicts.length > 0) {
                    newConflicts[dayIndex] = conflicts;
                    hasAnyConflict = true;
                }
            }
        }
        setConflictingBlocks(newConflicts);
        return hasAnyConflict;
    }, []);

    useEffect(() => {

        function checkIfScheduleHasValues(sch) {
            if (!sch || Object.keys(sch).length === 0) return false;
            for (let i = 0; i < 7; i++) {
                const day = sch[i];
                if (day && Array.isArray(day.blocks) && day.blocks.length > 0) {
                    return true;
                }
            }
            return false;
        }

        if (checkIfScheduleHasValues(schedule)) {
            setAvailabilitySchedule(schedule);
            updateConflicts(schedule);
        } else {
            // If no schedule is provided or all days are empty, initialize with default and inform parent.
            setAvailabilitySchedule(DEFAULT_SCHEDULE);
            updateConflicts(DEFAULT_SCHEDULE);
            onScheduleChange(DEFAULT_SCHEDULE);
        }
    }, [schedule, onScheduleChange, updateConflicts]);

    const handleDayToggle = (dayIndex) => {
        const currentDaySchedule = availabilitySchedule[dayIndex];
        const isCurrentlyAvailable = isAvailable(currentDaySchedule);
        
        let newSchedule;
        if (isCurrentlyAvailable) {
            // Make unavailable by clearing blocks
            newSchedule = {
                ...availabilitySchedule,
                [dayIndex]: {
                    blocks: []
                }
            };
        } else {
            // Make available by adding a default block
            newSchedule = {
                ...availabilitySchedule,
                [dayIndex]: {
                    blocks: [{ start: '09:00', end: '17:00' }]
                }
            };
        }
        
        setAvailabilitySchedule(newSchedule);
        const hasConflicts = updateConflicts(newSchedule);

        if (!hasConflicts) {
            onScheduleChange(newSchedule);
        }
    };

    const handleTimeChange = (dayIndex, blockIndex, field, value) => {
        const time24 = field === 'start' || field === 'end' ? parseTime(value) : value;
        const newSchedule = {
            ...availabilitySchedule,
            [dayIndex]: {
                blocks: availabilitySchedule[dayIndex].blocks.map((block, index) =>
                    index === blockIndex ? { ...block, [field]: time24 } : block
                )
            }
        };
        setAvailabilitySchedule(newSchedule);
        const hasConflicts = updateConflicts(newSchedule);

        if (!hasConflicts) {
            onScheduleChange(newSchedule);
        }
    };

    const addTimeBlock = (dayIndex) => {
        const existingBlocks = availabilitySchedule[dayIndex].blocks;
        let newBlockStart = '09:00';
        let newBlockEnd = '17:00';
        const isNewBlock = existingBlocks.length > 0;
        if (isNewBlock) {
            // Find the latest end time among existing blocks
            const latestEndTime = existingBlocks.reduce((latest, block) => {
                const currentEndMinutes = timeToMinutes(block.end);
                const latestMinutes = timeToMinutes(latest);
                return currentEndMinutes > latestMinutes ? block.end : latest;
            }, '00:00');

            // Add 15 minutes to the latest end time
            const latestEndMinutes = timeToMinutes(latestEndTime);
            const newStartMinutes = latestEndMinutes + 15;
            const newEndMinutes = newStartMinutes + 120; // 2 hours default duration

            // Convert back to time format
            const startHours = Math.floor(newStartMinutes / 60);
            const startMins = newStartMinutes % 60;
            const endHours = Math.floor(newEndMinutes / 60);
            const endMins = newEndMinutes % 60;

            newBlockStart = `${startHours.toString().padStart(2, '0')}:${startMins.toString().padStart(2, '0')}`;
            newBlockEnd = `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
        }

        const newSchedule = {
            ...availabilitySchedule,
            [dayIndex]: {
                blocks: [...availabilitySchedule[dayIndex].blocks, { start: newBlockStart, end: newBlockEnd }]
            }
        };
        setAvailabilitySchedule(newSchedule);
        const hasConflicts = updateConflicts(newSchedule);

        if (!hasConflicts) {
            onScheduleChange(newSchedule);
        }
    };

    const removeTimeBlock = (dayIndex, blockIndex) => {
        const isLastBlock = availabilitySchedule[dayIndex].blocks.length === 1;

        if (isLastBlock) {
            // If it's the last block, make day unavailable by clearing blocks
            const newSchedule = {
                ...availabilitySchedule,
                [dayIndex]: {
                    blocks: []
                }
            };
            setAvailabilitySchedule(newSchedule);
            updateConflicts(newSchedule);
            onScheduleChange(newSchedule);
            return
        }

        // Remove the specific block
        const newSchedule = {
            ...availabilitySchedule,
            [dayIndex]: {
                blocks: availabilitySchedule[dayIndex].blocks.filter((_, index) => index !== blockIndex)
            }
        };
        setAvailabilitySchedule(newSchedule);
        const hasConflicts = updateConflicts(newSchedule);

        if (!hasConflicts) {
            onScheduleChange(newSchedule);
        }

    };

    const copyToAll = (sourceDayIndex) => {
        const sourceDay = availabilitySchedule[sourceDayIndex];
        const newSchedule = {};

        for (let i = 0; i < 7; i++) {
            newSchedule[i] = {
                blocks: [...sourceDay.blocks]
            };
        }

        setAvailabilitySchedule(newSchedule);
        const hasConflicts = updateConflicts(newSchedule);

        if (!hasConflicts) {
            onScheduleChange(newSchedule);
        }
    };


    return (
        <Box>
            <Text fontSize="lg" fontWeight="medium" mb={4}>
                Available Hours
            </Text>

            <VStack spacing={4} align="stretch">
                {DAYS_OF_WEEK.map((dayName, dayIndex) => {
                    const dayIsAvailable = isAvailable(availabilitySchedule[dayIndex]);

                    return (
                        <Box key={dayIndex} p={4} border="1px" borderColor="gray.200" borderRadius="md">
                            <Flex align="center" justify="space-between" mb={3}>
                                <Checkbox
                                    isChecked={dayIsAvailable}
                                    onChange={() => handleDayToggle(dayIndex)}
                                    fontWeight="medium"
                                    size="lg"
                                >
                                    {dayName}
                                </Checkbox>

                                {!dayIsAvailable && (
                                    <Text color="gray.500" fontSize="sm">
                                        Unavailable - No time blocks set
                                    </Text>
                                )}
                            </Flex>

                            {dayIsAvailable && (
                                <Stack spacing={3}>
                                    {availabilitySchedule[dayIndex].blocks.map((block, blockIndex) => {
                                        const isConflicting = conflictingBlocks[dayIndex]?.includes(blockIndex);
                                        return (
                                            <HStack key={blockIndex} spacing={3}>
                                                <Input
                                                    type="time"
                                                    value={block.start}
                                                    onChange={(e) => handleTimeChange(dayIndex, blockIndex, 'start', e.target.value)}
                                                    size="sm"
                                                    width="120px"
                                                    borderColor={isConflicting ? "red.300" : "gray.200"}
                                                    _focus={{ borderColor: isConflicting ? "red.500" : "blue.500" }}
                                                />
                                                <Text>-</Text>
                                                <Input
                                                    type="time"
                                                    value={block.end}
                                                    onChange={(e) => handleTimeChange(dayIndex, blockIndex, 'end', e.target.value)}
                                                    size="sm"
                                                    width="120px"
                                                    borderColor={isConflicting ? "red.300" : "gray.200"}
                                                    _focus={{ borderColor: isConflicting ? "red.500" : "blue.500" }}
                                                />
                                                {isConflicting && (
                                                    <WarningIcon color="red.500" />
                                                )}
                                                <IconButton
                                                    icon={<DeleteIcon />}
                                                    size="sm"
                                                    variant="ghost"
                                                    colorScheme="red"
                                                    onClick={() => removeTimeBlock(dayIndex, blockIndex)}
                                                />
                                            </HStack>
                                        );
                                    })}

                                    <HStack spacing={2}>
                                        <Button
                                            leftIcon={<AddIcon />}
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => addTimeBlock(dayIndex)}
                                        >
                                            Add block
                                        </Button>
                                        <Button
                                            leftIcon={<CopyIcon />}
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => copyToAll(dayIndex)}
                                        >
                                            Copy to all
                                        </Button>
                                    </HStack>
                                </Stack>
                            )}
                        </Box>
                    );
                })}
            </VStack>
        </Box>
    );
};

export default AvailabilitySchedule;
